import { useCallback, useEffect, useState } from "react";
import { api } from "../lib/api";
import { Plus, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import MobileFilterPanel from "../components/MobileFilterPanel";
import { formatLocationHierarchy } from "../lib/location";

export default function Charges() {
  const [data, setData] = useState({ charges: [], metrics: {} });
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [adding, setAdding] = useState(false);
  const { user } = useAuth();
  const isAdmin = (user?.permission_keys || []).includes("tar.charges.manage");
  const canCreate = (user?.permission_keys || []).includes("tar.charges.create");

  const load = useCallback(async () => {
    setLoading(true);
    const { data } = await api.get("/charges");
    setData(data);
    setLoading(false);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const update = async (id, patch) => {
    await api.patch(`/charges/${id}`, patch);
    toast.success("Charge updated");
    load();
  };

  const remove = async (charge) => {
    if (!window.confirm(`Delete this charge for ${charge.room_name || "this room"}? This cannot be undone.`)) return;
    await api.delete(`/charges/${charge.id}`);
    toast.success("Charge deleted");
    load();
  };

  const filtered = data.charges.filter((c) => {
    if (filter === "all") return true;
    return c.status === filter;
  });

  const m = data.metrics || {};

  return (
    <div data-testid="charges-page">
      <div className="flex items-end justify-between mb-6">
        <div>
          <h1 className="font-chivo font-black text-4xl tracking-tight">
            Charges
          </h1>
          <p className="text-sm text-gray-600 mt-1">
            Charges raised from key non-returns and inspection damages. Add
            amount, then mark added when entered into resident&apos;s account.
          </p>
        </div>
        {canCreate && <button data-testid="add-charge" onClick={()=>setAdding(true)} className="flex min-h-11 items-center gap-2 rounded-lg bg-slate-950 px-4 font-bold text-white"><Plus size={17}/>Add charge</button>}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-0 border border-gray-200 bg-white mb-8">
        <Stat label="Total charges" value={m.total_count || 0} sub={fmt(m.total_amount)} />
        <Stat
          label="Added to account"
          value={m.added_count || 0}
          sub={fmt(m.added_amount)}
          accent="green"
        />
        <Stat
          label="Awaiting transfer"
          value={m.awaiting_count || 0}
          sub={fmt(m.awaiting_amount)}
          accent="red"
        />
      </div>

      <MobileFilterPanel className="gap-2 mb-3 flex-wrap" testId="charges-filters">
        {["all", "pending", "added_to_account"].map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            data-testid={`charges-filter-${f}`}
            className={`px-3 py-1.5 text-xs font-mono font-bold uppercase border ${
              filter === f
                ? "bg-black text-white border-black"
                : "bg-white border-gray-300 hover:border-black"
            }`}
          >
            {f === "all"
              ? "All"
              : f === "added_to_account"
              ? "Added"
              : "Awaiting"}
          </button>
        ))}
      </MobileFilterPanel>

      {loading ? (
        <div className="text-sm text-gray-500">Loading…</div>
      ) : filtered.length === 0 ? (
        <div className="bg-white border border-gray-200 p-8 text-center text-sm text-gray-500">
          No charges to display.
        </div>
      ) : (
        <div className="bg-white border border-gray-200 overflow-x-auto">
          <div className="min-w-[860px]">
          <div className="grid grid-cols-[1fr_2fr_120px_140px_180px_140px] gap-3 px-4 py-2 border-b border-gray-200 text-[10px] font-mono uppercase tracking-wider text-gray-500">
            <div>Room · Block</div>
            <div>Description</div>
            <div>Type</div>
            <div>Amount</div>
            <div>Status</div>
            <div></div>
          </div>
          {filtered.map((c) => (
            <ChargeRow key={c.id} c={c} onUpdate={update} onDelete={remove} canDelete={isAdmin} />
          ))}
          </div>
        </div>
      )}
      {adding && <AddCharge onClose={()=>setAdding(false)} onCreated={()=>{setAdding(false);load()}}/>}
    </div>
  );
}

function AddCharge({onClose,onCreated}) {
  const [rooms,setRooms]=useState([]),[form,setForm]=useState({room_id:"",description:"",amount:"",notes:""}),[saving,setSaving]=useState(false),[path,setPath]=useState({portfolio:"",site:"",block:"",flat:""});
  useEffect(()=>{api.get("/rooms").then(({data})=>setRooms(Array.isArray(data)?data:[])).catch(()=>toast.error("Locations could not be loaded"))},[]);
  const submit=async e=>{e.preventDefault();const amount=Number(form.amount);if(!form.room_id||!form.description.trim()||!Number.isFinite(amount)||amount<=0)return toast.error("Choose a Room and enter a reason and valid GBP amount");setSaving(true);try{await api.post("/charges",{...form,description:form.description.trim(),amount});toast.success("Manual charge created");onCreated()}catch(err){toast.error(err.response?.data?.detail||"Charge could not be created")}finally{setSaving(false)}};
  const unique=(items,idKey,nameKey)=>[...new Map(items.filter(item=>item[idKey]&&item[nameKey]).map(item=>[item[idKey],item[nameKey]])).entries()];
  const portfolios=unique(rooms,"portfolio_id","portfolio_name"),atPortfolio=path.portfolio?rooms.filter(r=>r.portfolio_id===path.portfolio):rooms,sites=unique(atPortfolio,"site_id","site_name"),atSite=path.site?atPortfolio.filter(r=>r.site_id===path.site):[],blocks=unique(atSite,"block_id","block_name"),atBlock=path.block?atSite.filter(r=>r.block_id===path.block):[],flats=unique(atBlock,"flat_id","flat_name"),atFlat=path.flat?atBlock.filter(r=>r.flat_id===path.flat):[],roomOptions=atFlat.slice(0,100);
  const choose=(key,value)=>{const order=["portfolio","site","block","flat"],next={...path,[key]:value};order.slice(order.indexOf(key)+1).forEach(item=>next[item]="");setPath(next);setForm(v=>({...v,room_id:""}))};
  return <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" role="dialog" aria-modal="true" aria-label="Add charge"><form onSubmit={submit} className="max-h-[95vh] w-full max-w-2xl overflow-auto rounded-xl bg-white shadow-xl"><header className="flex items-center justify-between border-b p-5"><div><h2 className="text-xl font-black">Add charge</h2><p className="text-sm text-slate-500">Narrow the physical hierarchy to choose a Room.</p></div><button type="button" onClick={onClose} aria-label="Close"><X/></button></header><div className="grid gap-4 p-5"><div className="grid gap-3 sm:grid-cols-2">{portfolios.length>0&&<Picker label="Portfolio" value={path.portfolio} onChange={value=>choose("portfolio",value)} options={portfolios}/>}<Picker label="Site" value={path.site} onChange={value=>choose("site",value)} options={sites}/><Picker label="Block" value={path.block} onChange={value=>choose("block",value)} options={blocks} disabled={!path.site}/><Picker label="Flat" value={path.flat} onChange={value=>choose("flat",value)} options={flats} disabled={!path.block}/></div><label className="text-sm font-semibold">Room<select data-testid="charge-room" required value={form.room_id} onChange={e=>setForm(v=>({...v,room_id:e.target.value}))} disabled={!path.flat} className="mt-1 min-h-11 w-full rounded border bg-white px-3"><option value="">Choose Room</option>{roomOptions.map(r=><option key={r.id} value={r.id}>{r.name||r.room_name}</option>)}</select></label>{form.room_id&&<p className="rounded bg-slate-50 p-3 text-sm">Selected: <strong>{formatLocationHierarchy({...rooms.find(r=>r.id===form.room_id),room_name:rooms.find(r=>r.id===form.room_id)?.name})}</strong></p>}<label className="text-sm font-semibold">Description / reason<input required value={form.description} onChange={e=>setForm(v=>({...v,description:e.target.value}))} className="mt-1 min-h-11 w-full rounded border px-3"/></label><label className="text-sm font-semibold">Amount (GBP)<input data-testid="charge-amount" required type="number" min="0.01" step="0.01" value={form.amount} onChange={e=>setForm(v=>({...v,amount:e.target.value}))} className="mt-1 min-h-11 w-full rounded border px-3"/></label><label className="text-sm font-semibold">Notes (optional)<textarea value={form.notes} onChange={e=>setForm(v=>({...v,notes:e.target.value}))} className="mt-1 min-h-24 w-full rounded border p-3"/></label></div><footer className="flex justify-end gap-2 border-t p-4"><button type="button" onClick={onClose} className="min-h-11 rounded border px-4">Cancel</button><button disabled={saving} className="min-h-11 rounded bg-slate-950 px-4 font-bold text-white disabled:opacity-50">{saving?"Creating…":"Create charge"}</button></footer></form></div>
}

function Picker({label,value,onChange,options,disabled}) { return <label className="text-sm font-semibold">{label}<select value={value} disabled={disabled} onChange={event=>onChange(event.target.value)} className="mt-1 min-h-11 w-full rounded border bg-white px-3 disabled:bg-slate-100"><option value="">Choose {label}</option>{options.map(([id,name])=><option key={id} value={id}>{name}</option>)}</select></label>; }

function ChargeRow({ c, onUpdate, onDelete, canDelete }) {
  const [amount, setAmount] = useState(c.amount ?? "");
  const [editing, setEditing] = useState(false);

  const save = () => {
    const parsedAmount = amount === "" ? null : parseFloat(amount);
    if (parsedAmount !== null && Number.isNaN(parsedAmount)) {
      toast.error("Enter a valid amount or leave it blank.");
      return;
    }
    onUpdate(c.id, { amount: parsedAmount });
    setEditing(false);
  };

  return (
    <div
      className="grid grid-cols-[1fr_2fr_120px_140px_180px_140px] gap-3 px-4 py-3 border-b border-gray-100 last:border-b-0 items-center"
      data-testid={`charge-row-${c.id}`}
    >
      <div className="text-sm">
        <div className="font-chivo font-bold">{c.room_name}</div>
        <div className="text-[11px] font-mono text-gray-500">
          {c.block_name}
        </div>
      </div>
      <div className="text-sm">{c.description}</div>
      <div>
        <span className="text-[10px] font-mono font-bold uppercase tracking-wider border border-gray-300 px-1.5 py-0.5">
          {c.type}
        </span>
      </div>
      <div>
        {editing ? (
          <div className="flex gap-1">
            <input
              type="number"
              step="0.01"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              data-testid={`charge-amount-input-${c.id}`}
              className="w-24 border border-gray-300 px-2 py-1 text-sm focus:outline-none focus:border-black"
            />
            <button
              onClick={save}
              data-testid={`charge-amount-save-${c.id}`}
              className="text-[10px] font-mono font-bold uppercase border border-black bg-black text-white px-2"
            >
              Save
            </button>
          </div>
        ) : (
          <button
            onClick={() => setEditing(true)}
            data-testid={`charge-amount-edit-${c.id}`}
            className="text-sm font-chivo font-bold hover:underline"
          >
            {c.amount != null ? fmt(c.amount) : "Add amount"}
          </button>
        )}
      </div>
      <div>
        {c.status === "added_to_account" ? (
          <div className="text-[11px] font-mono text-green-700">
            Added by {c.added_by}
            <br />
            {c.added_at ? new Date(c.added_at).toLocaleDateString() : ""}
          </div>
        ) : (
          <span className="text-[11px] font-mono uppercase tracking-wider text-amber-700">
            Awaiting transfer
          </span>
        )}
      </div>
      <div className="text-right flex justify-end gap-2">
        {c.status === "pending" ? (
          <button
            onClick={() => onUpdate(c.id, { status: "added_to_account" })}
            data-testid={`charge-mark-added-${c.id}`}
            disabled={c.amount == null}
            className="text-[10px] font-mono font-bold uppercase border border-black bg-black text-white px-2 py-1 disabled:opacity-30 disabled:cursor-not-allowed"
            title={c.amount == null ? "Add amount first" : ""}
          >
            Mark added
          </button>
        ) : (
          <button
            onClick={() => onUpdate(c.id, { status: "pending" })}
            data-testid={`charge-revert-${c.id}`}
            className="text-[10px] font-mono font-bold uppercase border border-gray-300 hover:border-black px-2 py-1"
          >
            Revert
          </button>
        )}
        {canDelete && (
          <button
            onClick={() => onDelete(c)}
            data-testid={`charge-delete-${c.id}`}
            className="text-[10px] font-mono font-bold uppercase border border-red-200 text-red-700 hover:border-red-500 px-2 py-1 inline-flex items-center gap-1"
            title="Delete charge"
          >
            <Trash2 className="w-3 h-3" />
            Delete
          </button>
        )}
      </div>
    </div>
  );
}

function Stat({ label, value, sub, accent }) {
  const colors = {
    green: "text-green-700",
    red: "text-amber-700",
  };
  return (
    <div className="border-r border-gray-200 last:border-r-0 p-5">
      <div className="text-xs font-mono uppercase tracking-wider text-gray-500">
        {label}
      </div>
      <div
        className={`font-chivo font-black text-4xl mt-2 ${
          colors[accent] || "text-black"
        }`}
      >
        {value}
      </div>
      <div className="text-xs font-mono text-gray-500 mt-1">{sub || ""}</div>
    </div>
  );
}

function fmt(v) {
  if (v == null) return "—";
  return new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency: "GBP",
    minimumFractionDigits: 2,
  }).format(v);
}
